import React, { useEffect } from 'react'

import { ProductIds, getUserBenefitProductClass } from 'core'
import finishSignupApi, {
  getLegalDocuments,
  isLegalDocumentLastStep,
} from 'core/application/employeeSignup/legalDocuments'
import ReimbursementProduct from 'core/domain/benefit/reimbursementProduct'

import CategoryItemPageHeader from 'common/components/onBoarding/Common'

import { useLoginUserV2 } from 'common/hooks/useLoginUser'

import { Col, Row, Skeleton } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

import LegalDocumentIcon from 'assets/images/icons/legal-documents-document.svg'
import NextIcon from 'assets/images/icons/legal-documents-next.svg'
import PaymentIcon from 'assets/images/icons/legal-documents-payment.svg'
import PeopleKeepIcon from 'assets/images/new-peoplekeep-lite-logo.svg'

import { BaseComponentProps } from '../models'

export type LegalDocumentsProps = {
  numberOfQuestions: number
  currentQuestion: number
  title?: string
  questionText?: string
  productQuestion?: string
}

const LegalDocuments: React.FC<BaseComponentProps<LegalDocumentsProps>> = (props) => {
  const { bindSubmitFormHandler } = props
  const { user, reloadUser } = useLoginUserV2()

  const { data: legalDocuments } = useQueryRequestV2({
    queryFn: () => getLegalDocuments(),
  })

  const { data: isLegalDocumentFinalStep } = useQueryRequestV2({
    queryFn: () => isLegalDocumentLastStep(),
  })
  const { data: productClassDetails } = useQueryRequestV2({
    queryFn: () => getUserBenefitProductClass(),
  })
  const { sendCommand: sendFinishSignup } = useCommandRequestV2(({}) => finishSignupApi())

  useEffect(() => {
    props.setIsFormValid(false)
  }, [])

  const handleScroll = (e: any) => {
    const bottomCeil = e.target.scrollHeight - Math.ceil(e.target.scrollTop) === e.target.clientHeight
    const bottomFloor = e.target.scrollHeight - Math.floor(e.target.scrollTop) === e.target.clientHeight
    if (bottomCeil || bottomFloor) {
      props.hasDirtyData(true)
      props.setIsFormValid(true)
    }
  }

  const onSubmit = async () => {
    const result = await sendFinishSignup({})
    if (result.completed) {
      if (isLegalDocumentFinalStep) {
        await reloadUser()
        props.setIsSubmiting(false)
        props.finish()
      } else {
        props.setIsSubmiting(false)
        props.goNext()
      }
    } else {
      props.setIsSubmiting(false)
    }
  }

  bindSubmitFormHandler(onSubmit)

  const reimburseDescription =
    productClassDetails?.reimbursementProduct.id === ReimbursementProduct.PremiumPlusOutOfPocketExpenses
      ? 'individual health insurance and out-of-pocket'
      : 'individual health insurance'

  return (
    <div data-testid={'eesignup-legal-documents'}>
      <Skeleton loading={!legalDocuments || !productClassDetails}>
        {legalDocuments && productClassDetails && (
          <>
            <Row justify={window.innerWidth < 767 ? 'center' : 'start'} className="mt-32">
              <Col xs={24} sm={24} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }}>
                <CategoryItemPageHeader
                  title={props.state.title ?? 'Legal document'}
                  numberOfQuestions={props.state.numberOfQuestions}
                  currentQuestion={props.state.currentQuestion}
                  question={props.state.questionText ?? 'Read all information below in order to continue.'}
                />
              </Col>
            </Row>

            <Row justify={window.innerWidth < 767 ? 'center' : 'start'}>
              <Col xs={24} sm={24} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }}>
                <div className="ml-8">
                  {props.state.productQuestion && (
                    <Col className="mb-24 mt-16">
                      <h5 className="text-bold">{props.state.productQuestion}</h5>
                    </Col>
                  )}

                  {user.productId === ProductIds.IchraId ? (
                    <p>
                      Your employer sets aside an allowance for you that builds every month until the end of the year.
                      You can use this allowance to cover your <b>{reimburseDescription}</b> costs.
                    </p>
                  ) : (
                    <p>
                      Your employer sets aside an allowance for you to use until the end of the year. You can use this
                      allowance to cover your <b>{reimburseDescription}</b> costs.
                    </p>
                  )}

                  <Row className="mt-32">
                    <Col span={8}>
                      <Row className="mb-24 legal-documents-icons-wrapper">
                        <Col span={12}>
                          <img src={LegalDocumentIcon} alt={'icon'} />
                        </Col>
                        <Col span={12}>
                          <img src={NextIcon} className="mt-16" alt={'icon'} />
                        </Col>
                      </Row>
                      <p>You submit a receipt from your eligible medical expense.</p>
                    </Col>
                    <Col span={8}>
                      <Row className="mb-24 legal-documents-icons-wrapper">
                        <Col span={12}>
                          <img src={PeopleKeepIcon} alt={'icon'} />
                        </Col>
                        <Col span={12}>
                          <img src={NextIcon} className="mt-16" alt={'icon'} />
                        </Col>
                      </Row>
                      <p>PeopleKeep verifies your medical expense.</p>
                    </Col>
                    <Col span={8}>
                      <Row className="mb-24 legal-documents-icons-wrapper">
                        <Col span={12}>
                          <img src={PaymentIcon} alt={'icon'} />
                        </Col>
                      </Row>
                      <p>Your employer pays you back tax-free!</p>
                    </Col>
                  </Row>
                  <p className="mt-16">
                    Your plan starts on{' '}
                    <span className="primary-text">{parseZoneFormat(legalDocuments.benefitStartDate)}</span>
                    .
                    <br />
                    Please contact <span className="primary-text">{legalDocuments.primaryAdminFirstName}</span>{' '}
                    <span className="primary-text">{legalDocuments.primaryAdminLastName}</span> at{' '}
                    <span className="primary-text">{legalDocuments.primaryAdminEmail}</span> for more information.
                  </p>
                </div>
                <div
                  onScroll={handleScroll}
                  className="scrollable-textbox mb-16 mt-32"
                  dangerouslySetInnerHTML={{ __html: legalDocuments.legalDocumentsContentHtml }}
                ></div>
                <div>
                  <p>Read all information above to continue.</p>
                </div>
              </Col>
            </Row>
          </>
        )}
      </Skeleton>
    </div>
  )
}

export default LegalDocuments
